import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Typography,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Table,
  Paper,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CloseIcon from "@mui/icons-material/Close";
import { OrderProduct } from "../../../../../models/Order/OrderProduct";
import {
  FILTER_PRODUCTS_TYPE_OPTIONS,
  FilterProductsType,
} from "../../../../../models/Products/Product";
import { useStores } from "../../../use-stores";
import { SortType } from "../../../../../models/SortType";
import { observer } from "mobx-react";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import FormSmallTextField from "../../../../../views/components/common/form/FormTextField";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";

interface AddProductsProps {
  list?: OrderProduct[];
  onSubmit: (products: OrderProduct[]) => void;
  onClose: () => void;
  isLoading?: boolean;
  handleInvoiceQuantity?: boolean;
}

function AddProductPopup(props: AddProductsProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<OrderProduct[]>([]);
  const { productStore, productListingStore } = useStores();
  const [filteredProducts, setFilteredProducts] = useState<OrderProduct[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const categoryOptions = useMemo(
    () =>
      (productStore.categories || []).map((category) => ({
        id: String(category.id),
        value: category.name,
      })),
    [productStore.categories]
  );
  useEffect(() => {
    if (productStore.categories.length === 0) {
      productStore.getCategories().catch(() => { });
    }

    if (props.list && props.list.length > 0) {
      setFilteredProducts(props.list);
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Category multi-select renders options in a MUI Popper (portal), not under filterBarRef.
      const categoryPopper = document.getElementById(
        "app-search-dropdown-multi-popper",
      );
      if (filterBarRef.current?.contains(target)) return;
      if (categoryPopper?.contains(target)) return;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        handleDropdownClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [productStore, props.list]);

  const handleSelectProduct = (product: OrderProduct) => {
    const index = selectedProducts.findIndex((p) => p.id === product.id);
    if (index === -1) {
      product.avail_invoice_qty = product.quantity - product.invoiced_quantity;
      setSelectedProducts([...selectedProducts, product]);
    } else {
      const updatedProducts = [...selectedProducts];
      updatedProducts.splice(index, 1);
      setSelectedProducts(updatedProducts);
    }
  };

  const fetchProductsFromApi = (query: string) => {
    const parsedMin = Number(minPrice);
    const parsedMax = Number(maxPrice);
    const hasMinPrice = minPrice.trim() !== "" && !Number.isNaN(parsedMin);
    const hasMaxPrice = maxPrice.trim() !== "" && !Number.isNaN(parsedMax);

    const params = {
      page_no: 1,
      sortBy: "id",
      sortType: SortType.ASC,
      ...(query.trim().length > 0 ? { name: query.trim() } : {}),
      ...(hasMinPrice ? { min_price: parsedMin } : {}),
      ...(hasMaxPrice ? { max_price: parsedMax } : {}),
      ...(selectedCategoryIds.length > 0
        ? { category_id: selectedCategoryIds.map(Number) }
        : {}),
      ...(productListingStore.filterProductsType ===
      FilterProductsType.FastProcurementProduct
        ? { fast_procurement: true }
        : {}),
    };

    productStore
      .getProducts(params)
      .then(() => {
        setFilteredProducts(
          productStore.getOrderProductsFromProducts(productStore.products)
        );
      })
      .catch(() => {
        setFilteredProducts([]);
      });
    setIsDropdownOpen(true);
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    const hasMinPrice = minPrice.trim() !== "";
    const hasMaxPrice = maxPrice.trim() !== "";
    const hasRangeFilter = hasMinPrice || hasMaxPrice;

    if (!props.list && (query.length > 2 || hasRangeFilter)) {
      debounceTimeoutRef.current = setTimeout(() => {
        fetchProductsFromApi(query);
      }, 500);
    } else {
      if (props?.list?.length === 0) return;
      const filtered = props?.list?.filter(
        (product: OrderProduct) => {
          const matchesQuery =
            product.name!.toLowerCase().includes(query.toLowerCase()) ||
            product.category_id.toString().includes(query);
          const matchesCategory =
            selectedCategoryIds.length === 0 ||
            selectedCategoryIds.includes(String(product.category_id));
          return matchesQuery && matchesCategory;
        }
      );
      setFilteredProducts(filtered || []);
      setIsDropdownOpen(true);
    }
  };

  const handleApplyRangeFilter = () => {
    if (props.list) return;
    fetchProductsFromApi(searchTerm);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setMinPrice("");
    setMaxPrice("");
    setSelectedCategoryIds([]);
    productListingStore.setFilterProductsType(FilterProductsType.AllProducts);
    if (!props.list) {
      fetchProductsFromApi("");
    } else {
      setFilteredProducts(props.list || []);
      setIsDropdownOpen(true);
    }
  };

  const removeProduct = (product: OrderProduct) => {
    const index = selectedProducts.findIndex((p) => p.id === product.id);
    const updatedProducts = [...selectedProducts];
    updatedProducts.splice(index, 1);
    setSelectedProducts(updatedProducts);
  };

  const handleClearAll = () => {
    setSelectedProducts([]);
  };

  const updateConfirmedProducts = () => {
    if (selectedProducts.length === 0) {
      return;
    }

    const updated_products: OrderProduct[] = selectedProducts.map((product) => {
      return {
        ...product,
        quantity: props.handleInvoiceQuantity
          ? product.avail_invoice_qty || 1
          : product.quantity || 1,
      };
    });

    props.onSubmit(updated_products);
  };

  const updateProductQuantity = (value: number, product: OrderProduct) => {
    const index = selectedProducts.findIndex(
      (selectedProduct) => selectedProduct.id === product.id
    );
    if (index !== -1) {
      if (props.handleInvoiceQuantity) {
        const updatedProduct = [...selectedProducts];
        updatedProduct[index].avail_invoice_qty = value;
        setSelectedProducts(updatedProduct);
      } else {
        const updatedProduct = [...selectedProducts];
        updatedProduct[index].quantity = value;
        setSelectedProducts(updatedProduct);
      }
    }
  };

  const handleSearchBarClick = () => {
    if (filteredProducts.length > 0) {
      setIsDropdownOpen(true);
    }
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  return (
    <Dialog
      open
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "63.125rem",
          width: "100%",
          height: "100%",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingX: 5,
          paddingBottom: 1,
          paddingTop: 3.5,
        }}
      >
        <CustomTypography variant="heading4" fontWeight={700}>
          Add Products
        </CustomTypography>
        <IconButton
          sx={{
            color: "black",
            "&:hover": {
              backgroundColor: "grey",
            },
          }}
          size="small"
          onClick={() => {
            setSelectedProducts([]);
            props.onClose();
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        ref={filterBarRef}
        sx={{ paddingX: 5, paddingBottom: 0, paddingTop: 2.5 }}
      >
        <CustomTextField
          value={searchTerm}
          onSearch={(query) => handleSearch(query)}
          width={"100%"}
          onClick={handleSearchBarClick}
        />
        {!props.list && (
          <Box>
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: "12rem" }}>
                <AppSearchDropdownMulti
                  title="Select Category"
                  fontSize="0.8rem"
                  isMultiSelect={true}
                  value={selectedCategoryIds.map((id) => ({
                    id,
                    value:
                      categoryOptions.find(
                        (category) => String(category.id) === String(id)
                      )?.value || String(id),
                  }))}
                  list={categoryOptions}
                  local={true}
                  handleSelect={(selectedItems) =>
                    setSelectedCategoryIds(
                      selectedItems.map((item) => String(item.id))
                    )
                  }
                />
              </Box>
              <Box sx={{ minWidth: "12rem" }}>
                <AppDropDown
                  title="Product Classification Type"
                  fontSize="0.8rem"
                  isMultiSelect={false}
                  value={productListingStore.filterProductsType}
                  list={FILTER_PRODUCTS_TYPE_OPTIONS}
                  onSingleSelectionChange={(id) =>
                    productListingStore.setFilterProductsType(
                      id === FilterProductsType.FastProcurementProduct
                        ? FilterProductsType.FastProcurementProduct
                        : FilterProductsType.AllProducts,
                    )
                  }
                />
              </Box>
              <FormSmallTextField
                variant="outlined"
                color="secondary"
                type="number"
                placeholder="Min Price"
                value={minPrice}
                sx={{ maxWidth: "10rem", mb: "0" }}
                onChange={(_, value) => setMinPrice(value || "")}
              />
              <FormSmallTextField
                variant="outlined"
                color="secondary"
                type="number"
                placeholder="Max Price"
                value={maxPrice}
                sx={{ maxWidth: "10rem", mb: "0" }}
                onChange={(_, value) => setMaxPrice(value || "")}
              />
              <AppButton
                label="Apply"
                variant="transparentPrimary"
                onClick={handleApplyRangeFilter}
                customStyles={{ minWidth: 90 }}
              />
              <AppButton
                label="Clear"
                variant="grayscale"
                onClick={handleClearFilters}
                customStyles={{ minWidth: 90 }}
              />
            </Box>
            {selectedCategoryIds.length > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  width: "100%",
                  display: "flex",
                  flexWrap: "nowrap",
                  gap: 0.5,
                  overflowX: "auto",
                  overflowY: "hidden",
                  whiteSpace: "nowrap",
                  pb: 0.5,
                  "&::-webkit-scrollbar": {
                    height: 6,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,0.2)",
                    borderRadius: 4,
                  },
                }}
              >
                {selectedCategoryIds.map((id) => {
                  const category = categoryOptions.find(
                    (item) => String(item.id) === String(id)
                  );
                  if (!category) return null;
                  return (
                    <Chip
                      key={`category-${id}`}
                      size="small"
                      label={category.value}
                      onDelete={() =>
                        setSelectedCategoryIds((prev) =>
                          prev.filter((selectedId) => selectedId !== id)
                        )
                      }
                      sx={{
                        fontSize: "0.625rem",
                        height: "24px",
                        flexShrink: 0,
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <DialogContent sx={{ paddingX: 5, paddingTop: 0 }}>
        {isDropdownOpen && (
          <Box
            ref={dropdownRef}
            sx={{
              paddingX: 0,
              paddingTop: 0,
              paddingBottom: 0,
              position: "relative",
            }}
          >
            <Box
              sx={{
                maxHeight: 400,
                overflowY: "auto",
                boxShadow: 1,
                backgroundColor: "white",
                borderRadius: 2,
                marginTop: 0,
                padding: 2,
                position: "absolute",
                zIndex: 2,
                width: "100%",
              }}
            >
              {filteredProducts.map((product: any) => (
                <Box
                  key={product.id}
                  display="flex"
                  alignItems="center"
                  p={1}
                  borderRadius={1}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectProduct(product);
                    setSearchTerm("");
                  }}
                >
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                    }}
                    control={
                      <Checkbox
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleSelectProduct(product)}
                        checked={selectedProducts.some(
                          (selectedProduct) => selectedProduct.id === product.id
                        )}
                        color="primary"
                      />
                    }
                    label={""}
                  />
                  <Box
                    sx={{
                      background: "rgba(242, 242, 242, 1)",
                      padding: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginRight: 2,
                      minHeight: "3.438rem",
                      maxHeight: "3.438rem",
                      borderRadius: "0.375rem",
                    }}
                  >
                    <img
                      style={{
                        maxHeight: "2.375rem",
                        minHeight: "2.375rem",
                        maxWidth: "2.375rem",
                        minWidth: "2.375rem",
                      }}
                      src={product.image_url}
                      alt={product.name}
                    />
                  </Box>
                  <Box sx={{ marginRight: 1.5 }}>
                    <Typography fontWeight="bold">{product.name}</Typography>
                    <Box display="flex" gap={2}>
                      <Typography variant="body2" color="text.secondary">
                        Category ID: {product.category_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Price: {product.selling_price}
                      </Typography>
                      {Array.isArray(product.stock_by_city) &&
                        product.stock_by_city.length > 0 && (
                          <Box
                            display="flex"
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            {product.stock_by_city.map(
                              (stock: any, index: number) => (
                                <Chip
                                  key={`${product.id}-stock-${stock.city}-${index}`}
                                  size="small"
                                  label={`${stock.city}: ${stock.quantity}`}
                                  sx={{
                                    fontSize: "0.625rem",
                                    height: "20px",
                                    backgroundColor: "rgba(0, 128, 0, 0.08)",
                                    color: "rgba(0, 100, 0, 1)",
                                  }}
                                />
                              )
                            )}
                          </Box>
                        )}
                    </Box>
                  </Box>
                </Box>
              ))}
              {filteredProducts.length === 0 && (
                <Typography sx={{ padding: 2 }} color="text.secondary">
                  No products found.
                </Typography>
              )}
            </Box>
          </Box>
        )}
        <Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={1}
          >
            <Box sx={{ width: "100%" }}>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 2, maxHeight: "40vh", minHeight: "40vh" }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        ID
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        PRODUCT
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        PRICE
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        GST%
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="3.125rem" fontWeight={700}>
                        QUANTITY
                      </AppTableHeaderCell>
                      <AppTableHeaderCell minWidth="1.875rem" fontWeight={700}>
                        REMOVE
                      </AppTableHeaderCell>
                    </TableRow>
                  </TableHead>

                  {selectedProducts.length === 0 ? (
                    <TableBody>
                      <TableRow>
                        <TableCell
                          colSpan={12}
                          sx={{
                            borderBottom: "none",
                            padding: 3,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              height: "fit-content",
                              textAlign: "center",
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              fontWeight={700}
                              color="rgba(218, 218, 218, 1)"
                            >
                              NO PRODUCTS FOUND!
                            </CustomTypography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  ) : (
                    <TableBody>
                      {selectedProducts?.map((product: OrderProduct) => (
                        <TableRow key={product.id}>
                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {product.id}
                          </AppTableRowCell>

                          <AppTableRowCell
                            minWidth="5rem"
                            sx={{
                              padding: 1,
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <Box
                                sx={{
                                  background: "rgba(242, 242, 242, 1)",
                                  padding: 1,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  marginRight: 2,
                                  minHeight: "3.438rem",
                                  maxHeight: "3.438rem",
                                  borderRadius: "0.375rem",
                                }}
                              >
                                <img
                                  style={{
                                    maxHeight: "2.375rem",
                                    minHeight: "2.375rem",
                                    maxWidth: "2.375rem",
                                    minWidth: "2.375rem",
                                  }}
                                  src={product.image_url}
                                  alt={product.name}
                                />
                              </Box>
                              <Box sx={{ textAlign: "left" }}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={700}
                                  title={product.name}
                                  sx={{
                                    cursor: "default",
                                  }}
                                >
                                  {product.name}
                                </CustomTypography>

                                <CustomTypography
                                  variant="body3"
                                  fontWeight={500}
                                  color="rgba(111, 111, 111, 1)"
                                >
                                  Category ID: {product.category_id}
                                </CustomTypography>
                              </Box>
                            </Box>
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {product.selling_price}
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            {product.gst_percentage || "-"}
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="3.125rem" fontWeight={700}>
                            <FormSmallTextField
                              variant="outlined"
                              color="secondary"
                              type="number"
                              fontSize="0.875rem"
                              value={
                                props.handleInvoiceQuantity
                                  ? product.avail_invoice_qty || ""
                                  : product.quantity || ""
                              }
                              sx={{
                                maxWidth: "5rem",
                                mb: "0",
                                "& .MuiInputBase-input": {
                                  textAlign: "center",
                                },
                                "& input[type=number]": {
                                  textAlign: "center",
                                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button":
                                  {
                                    display: "none",
                                  },
                                  MozAppearance: "textfield",
                                },
                              }}
                              onChange={(id, value) =>
                                updateProductQuantity(Number(value), product)
                              }
                            />
                          </AppTableRowCell>

                          <AppTableRowCell minWidth="1.875rem" fontWeight={700}>
                            <IconButton
                              onClick={() => {
                                removeProduct(product);
                              }}
                            >
                              <DeleteIcon
                                sx={{ color: "rgba(255, 0, 130, 0.8)" }}
                              />
                            </IconButton>
                          </AppTableRowCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 3 }}>
          {props.isLoading ? (
            <AppButton
              label={
                <>
                  <CircularProgress size={16} color="inherit" /> ...Loading
                </>
              }
              variant="primaryBlock"
              customStyles={{
                padding: 3,
                paddingTop: 1,
                paddingBottom: 1,
                margin: 0,
                fontWeight: 500,
                fontSize: "1rem",
                marginBottom: "1rem",
              }}
              onClick={function (): void { }}
            />
          ) : (
            <AppButton
              label="Confirm Products"
              onClick={updateConfirmedProducts}
              disabled={selectedProducts.length === 0}
              variant="primaryBlock"
              customStyles={{
                padding: 3,
                paddingTop: 1,
                paddingBottom: 1,
                margin: 0,
                fontWeight: 500,
                fontSize: "1rem",
                marginBottom: "1rem",
              }}
            />
          )}

          <AppButton
            label="Clear All"
            onClick={handleClearAll}
            disabled={selectedProducts.length === 0}
            variant="grayscale"
            customStyles={{
              padding: 7.3,
              paddingTop: 1,
              paddingBottom: 1,
              margin: 0,
              fontWeight: 600,
              fontSize: "1rem",
              marginBottom: "1rem",
            }}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default observer(AddProductPopup);
